import RecordLabel from "components/RecordLabel";
import { getInitialQueryWithTableSettings } from "components/Table/tableUtils";
import { rolePermissionListKey } from "containers/Resources/screens/ResourcesSingle/ResourcesSingle";
import { SubrecordTable } from "containers/Resources/screens/ResourcesSingle/components/SubrecordTable";
import { SubrecordTableProps } from "containers/Resources/screens/ResourcesSingle/components/UserGitPublicKeyList";
import { Role } from "graphql/generated/graphql";
import { useCallback, useMemo } from "react";
import { getRelativeUrl } from "utils/service";

interface RolePermissionSubrecordProps {
    updateRolePermissionQuery: (query: any) => void;
    rolePermissionInitialQuery: any;
}

export const RolePermissionList = ({
    resourceData,
    onResourceClick,
    createSubrecord,
    fetchResourceData,
    isFieldDisabled,
    updateRolePermissionQuery,
    rolePermissionInitialQuery,
}: SubrecordTableProps<Role> & RolePermissionSubrecordProps) => {
    const rolePermissionData = useMemo(
        () =>
            resourceData?.role_permission_list_role_id?.role_permission_list?.map((permission) => ({
                ...permission,
                deleted_at: permission?.role_id_role?.deleted_at,
                is_readonly: permission?.role_id_role?.is_readonly,
                resource_type: "role_permission",
            })),
        [resourceData]
    );
    const totalItemCount = resourceData?.role_permission_list_role_id?.role_permission_count || 0;
    const openRolePermissionForm = useCallback(
        (e, id, resource_type) =>
            onResourceClick({
                e,
                id,
                resource_type,
            }),
        [onResourceClick]
    );
    const columns = [
        {
            label: "Operation",
            key: "operation",
            shown: true,
            sortable: true,
            dto: ({ operation, id, resource_type }) => {
                const operationStr = operation ? operation : "All operations";

                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(resource_type, id)}
                        name={`${operationStr}`}
                        onClick={(e) => openRolePermissionForm(e, id, resource_type)}
                    />
                );
            },
        },
        {
            label: "Resource type",
            key: "table_type",
            shown: true,
            sortable: true,
            dto: ({ table_type, id, resource_type }) => {
                const typeStr = table_type ? table_type : "All types";
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(resource_type, id)}
                        name={`${typeStr}`}
                        onClick={(e) => openRolePermissionForm(e, id, resource_type)}
                    />
                );
            },
        },
        {
            label: "Project",
            key: "project_id",
            shown: true,
            sortable: true,
            dto: ({ id, project_id, project_id_project, resource_type }) => {
                const projectStr = project_id ? project_id_project?.name : "All projects";

                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(resource_type, id)}
                        name={`${projectStr}`}
                        onClick={(e) => openRolePermissionForm(e, id, resource_type)}
                    />
                );
            },
        },
        {
            label: "Object template",
            key: "object_template_id",
            shown: true,
            sortable: true,
            dto: ({
                id,
                table_type,
                object_template_id,
                object_template_id_object_template,
                resource_type,
            }) => {
                // object template scoping only applies to CUSTOM_OBJECT grants
                let templateStr = "";
                if (table_type === "CUSTOM_OBJECT") {
                    templateStr = object_template_id
                        ? object_template_id_object_template?.name
                        : "All object templates";
                }

                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(resource_type, id)}
                        name={`${templateStr}`}
                        onClick={(e) => openRolePermissionForm(e, id, resource_type)}
                    />
                );
            },
        },
    ];

    return (
        <SubrecordTable
            key="rolePermissionList"
            data={rolePermissionData as any[]}
            uniqueKey="rolePermissionList"
            title="Permissions"
            onAddClick={createSubrecord}
            isActionButtonDisabled={isFieldDisabled}
            refreshData={fetchResourceData}
            columns={columns}
            emptyListText="No permissions granted."
            initialQuery={getInitialQueryWithTableSettings(
                rolePermissionListKey,
                rolePermissionInitialQuery
            )}
            recordLabel="permission"
            updateQuery={updateRolePermissionQuery}
            totalItemCount={totalItemCount}
            tableSettingsKey={rolePermissionListKey}
            tableOptions={{ viewColumns: true }}
        />
    );
};
